import { type DefaultBodyType, MockedRequest, RestHandler } from "msw";
import { type APIDomainName, getEndpointString } from "../adapters/api.ts";
import { type DomainConfig } from "../config.ts";
// import { ResourceAdapterType } from "../util";
import { type RequestWrapperType } from "../util/requests/requestWrapper.ts";
import { buildMeasurementTypeHandlers } from "./MeasurementTypeMocks.ts";
import { buildReportingGroupHandlers } from "./ReportingGroupMocks.ts";
import { buildUserInterfaceConfigurationHandlers } from "./UserInterfaceConfigurationMocks.ts";
import { buildUserHandlers } from "./UserMocks.ts";
import { buildDeviceTypeHandlers } from "./DeviceTypeMocks.ts";
import { buildDeviceHandlers } from "./DeviceMocks.ts";
import { buildCustomAttributeHandlers } from "./CustomAttributeMocks.ts";
import { buildFacilityHandlers } from "./FacilityMocks.ts";
import { buildEquipmentHandlers } from "./EquipmentMocks.ts";
import { buildFormCategoryHandlers } from "./FormCategoryMocks.ts";

const getApiUrlWithEndpoint = ({
  url,
  endpoint,
  version,
}: WebApiDetailsType & Pick<RequestWrapperType, "endpoint">) =>
  `${url}${getEndpointString({ url, endpoint, version }) ?? ""}`;

/**
 * Holds constructed instances of all the handlers for the various APIs
 */
const restHandlers: DomainRestMockHandlers = {
  publicNodeAPI: [],
  nodeAPI: [],
  alertingAPI: [],
  dataEngAPI: [],
  elixirAPI: [],
  insightsAPI: [],
  dataPlatformSourceAPI: [],
  dataPlatformAdminAPI: [],
};

// IMPROVE: reference all handler builders for each resource, then create a full map all the way down to adapter methods
const buildRestAPIHandlers = (configData: DomainConfig) => {
  restHandlers.nodeAPI = [
    ...Object.values(buildMeasurementTypeHandlers(configData)),
    ...Object.values(buildReportingGroupHandlers(configData)),
    ...Object.values(buildUserInterfaceConfigurationHandlers(configData)),
    ...Object.values(buildUserHandlers(configData)),
    ...Object.values(buildDeviceTypeHandlers(configData)),
    ...Object.values(buildDeviceHandlers(configData)),
    ...Object.values(buildCustomAttributeHandlers(configData)),
    ...Object.values(buildFacilityHandlers(configData)),
    ...Object.values(buildEquipmentHandlers(configData)),
    ...Object.values(buildFormCategoryHandlers(configData)),
  ];
};

// WIP: load a specific resource, or even specific methods, given a spec
// const buildRestAPIHandlersV2 = (
//   configData: DomainConfig,
//   handlersLoadSpec: MockHandlersLoadSpec
// ) => {
//   return {};
// };

// WIP:
/**
 * @example
    {
      resources: [
        {
          resource: "equipment",
          methods: ["getList", "getOne"]
        }
      ]
    }

    @example
    {
      resources: [
        {
          resource: "equipment",
          methods: "*",
        },
      ];
    }

    @example
    {
      resources: "*";
    }
 */
// type MockHandlersLoadSpec = {
//   resources: "*" | MockHandlersResourceLoadSpec[];
// };

// IMPROVE: resource should be a specific string union of all available resources. methods are free-form.
// type MockHandlersResourceLoadSpec = {
//   resource: string;
//   methods: "*" | string[];
// };

type WebApiDetailsType = {
  url: string;
  version?: number;
};

type ResourceServiceMockHandlersType<
  TAdapter, // extends ResourceAdapterType<any>, TODO: not everything is a CRUD
  THandler = RestHandler<MockedRequest<DefaultBodyType>>,
> = Partial<{
  [key in keyof TAdapter]: THandler;
}>;

type DomainRestMockHandlers = {
  [key in APIDomainName]: Array<RestHandler<MockedRequest<DefaultBodyType>>>;
};

type BuildMockHandlersType<
  TAdapter, // extends ResourceAdapterType<any>, TODO: not everything is a CRUD
  THandler = RestHandler<MockedRequest<DefaultBodyType>>,
> = (
  configData: DomainConfig
) => ResourceServiceMockHandlersType<TAdapter, THandler>;

export { buildRestAPIHandlers, getApiUrlWithEndpoint, restHandlers };
export type {
  BuildMockHandlersType,
  DomainRestMockHandlers,
  ResourceServiceMockHandlersType,
  WebApiDetailsType,
};
